import { CONTACT } from "../../lib/contact";
import { useT } from "../../i18n/LanguageContext";

export default function Hero() {
    const { t } = useT();
    const stats = t("hero.stats");

    return (
        <section
            id="top"
            data-testid="hero-section"
            data-cta-source="hero"
            className="relative pt-28 md:pt-32 lg:pt-36 pb-20 md:pb-28 overflow-hidden"
        >
            <div
                className="absolute inset-0 -z-10 opacity-[0.55]"
                style={{
                    backgroundImage: 'url("/hero-bg.webp")',
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
                aria-hidden
            />
            <div className="absolute inset-0 -z-10 bg-gradient-to-b from-transparent via-[#F9F6F0]/60 to-[#F9F6F0]" aria-hidden />

            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
                    <div className="lg:col-span-8 animate-fade-up">
                        <p className="overline mb-8 flex items-center gap-3">
                            <span className="inline-block w-8 h-px bg-ink/60" />
                            {t("hero.overline")}
                        </p>
                        <h1 className="font-serif text-[2.4rem] leading-[1.04] sm:text-5xl md:text-6xl lg:text-[5.2rem] lg:leading-[1.02] tracking-tighter text-ink">
                            {t("hero.headlineA")}{" "}
                            <em className="italic font-light text-terracotta">
                                {t("hero.headlineEm")}
                            </em>{" "}
                            {t("hero.headlineB")}
                        </h1>
                        <p className="mt-8 max-w-2xl text-base md:text-lg lg:text-xl leading-relaxed text-secondary font-sans">
                            {t("hero.body")}
                        </p>
                        <div className="mt-10 flex flex-wrap items-center gap-4">
                            <a
                                href={CONTACT.calendlyHref}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="hero-calendly-button"
                                className="inline-flex items-center gap-3 bg-ink text-sand px-7 py-4 text-sm tracking-wide hover:bg-[var(--ink-soft)] transition-colors"
                            >
                                {t("hero.ctaCall")}
                                <span aria-hidden>→</span>
                            </a>
                            <a
                                href={CONTACT.whatsappHref}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="hero-whatsapp-button"
                                className="inline-flex items-center gap-3 border border-ink text-ink px-7 py-4 text-sm tracking-wide hover:bg-ink hover:text-sand transition-colors"
                            >
                                {t("hero.ctaWhatsapp")}
                            </a>
                            <a
                                href={CONTACT.emailHref}
                                data-testid="hero-email-link"
                                className="ml-1 text-sm text-secondary hover:text-ink link-underline"
                            >
                                {CONTACT.email}
                            </a>
                        </div>
                    </div>
                    <div className="lg:col-span-4 flex lg:justify-end">
                        <div className="grid grid-cols-2 gap-6 w-full max-w-md lg:max-w-none lg:w-[22rem]">
                            {stats.map((s) => (
                                <Stat key={s.label} k={s.k} label={s.label} />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

const Stat = ({ k, label }) => (
    <div className="border-t border-warm pt-4">
        <div className="font-serif text-2xl md:text-3xl lg:text-4xl tracking-tight text-ink">
            {k}
        </div>
        <div className="text-xs uppercase tracking-[0.18em] text-secondary mt-1">
            {label}
        </div>
    </div>
);
